/* Phase 55 — Brand & Model database.
 *
 * Curated model list per brand covering the Swiss used-car market.
 * Format: { "Brand": ["Model 1", "Model 2", ...] }
 *
 * Coverage policy:
 *  - Include modern lineups (post-2005) and current EV families.
 *  - Series names use the typical Swiss/German listing nomenclature
 *    (e.g. "3er" for BMW, "C-Klasse" for Mercedes, "Q3" for Audi).
 *  - Special editions (RS, AMG, M-Performance) get their own entries.
 *  - Free-text fallback ("Anderes Modell") handled by the UI layer —
 *    listings created with unknown models still work, the model field is
 *    just a free string on the listing document.
 *
 * The list is intentionally NOT exhaustive (>10 000 trims worldwide); the
 * goal is to cover ~95% of real Swiss used-car listings.
 */
export const BRAND_MODELS = {
  // ---------- GERMAN PREMIUM ----------
  "Audi": [
    "A1", "A3", "A4", "A4 Allroad", "A5", "A6", "A6 Allroad", "A7", "A8",
    "Q2", "Q3", "Q3 Sportback", "Q4 e-tron", "Q5", "Q5 Sportback", "Q7", "Q8",
    "TT", "TTS", "TT RS", "R8",
    "RS3", "RS4", "RS5", "RS6", "RS7", "RS Q3", "RS Q8",
    "S3", "S4", "S5", "S6", "S7", "S8", "SQ5", "SQ7", "SQ8",
    "e-tron", "e-tron GT", "RS e-tron GT",
  ],
  "BMW": [
    "1er", "2er", "2er Active Tourer", "2er Gran Coupé",
    "3er", "3er Touring", "4er", "4er Gran Coupé", "5er", "5er Touring", "6er", "7er", "8er",
    "X1", "X2", "X3", "X4", "X5", "X6", "X7", "XM",
    "Z4",
    "M2", "M3", "M4", "M5", "M6", "M8",
    "X3 M", "X4 M", "X5 M", "X6 M",
    "i3", "i4", "i5", "i7", "i8", "iX", "iX1", "iX3",
  ],
  "Mercedes-Benz": [
    "A-Klasse", "B-Klasse", "C-Klasse", "C-Klasse T-Modell", "CLA", "CLA Shooting Brake",
    "CLS", "E-Klasse", "E-Klasse T-Modell", "S-Klasse", "Maybach S-Klasse",
    "GLA", "GLB", "GLC", "GLC Coupé", "GLE", "GLE Coupé", "GLS", "G-Klasse",
    "SL", "SLC", "AMG GT", "AMG GT 4-Door",
    "V-Klasse", "Vito", "Sprinter", "Citan",
    "EQA", "EQB", "EQC", "EQE", "EQE SUV", "EQS", "EQS SUV", "EQV",
  ],
  "Porsche": [
    "911", "911 Carrera", "911 Turbo", "911 GT3", "911 GT2 RS",
    "718 Boxster", "718 Cayman", "718 Spyder",
    "Cayenne", "Cayenne Coupé", "Macan", "Panamera", "Panamera Sport Turismo",
    "Taycan", "Taycan Cross Turismo", "Taycan Sport Turismo",
  ],
  "Volkswagen": [
    "Polo", "Golf", "Golf Variant", "Golf GTI", "Golf R", "Golf Plus",
    "Jetta", "Passat", "Passat Variant", "Arteon", "Arteon Shooting Brake",
    "Beetle", "Scirocco", "T-Cross", "T-Roc", "T-Roc Cabrio",
    "Tiguan", "Tiguan Allspace", "Touareg", "Touran", "Sharan",
    "Caddy", "Multivan", "T6", "T6.1", "T7",
    "Up", "ID.3", "ID.4", "ID.5", "ID.7", "ID. Buzz",
  ],
  "Opel": [
    "Adam", "Corsa", "Astra", "Astra Sports Tourer", "Insignia",
    "Mokka", "Crossland", "Grandland", "Zafira", "Combo", "Vivaro", "Movano",
  ],
  "Smart": ["fortwo", "forfour", "Roadster", "#1", "#3"],

  // ---------- EV / NEW ENERGY ----------
  "Tesla": ["Model 3", "Model Y", "Model S", "Model X", "Cybertruck", "Roadster"],
  "Polestar": ["1", "2", "3", "4", "5"],
  "Lucid": ["Air", "Gravity"],
  "Rivian": ["R1T", "R1S"],
  "BYD": ["Atto 3", "Dolphin", "Han", "Seal", "Tang", "Sealion 7"],
  "NIO": ["ES6", "ES8", "ET5", "ET7", "EL6", "EL7"],

  // ---------- JAPANESE ----------
  "Toyota": [
    "Aygo", "Aygo X", "Yaris", "Yaris Cross", "Corolla", "Corolla Cross", "Corolla Touring Sports",
    "Camry", "C-HR", "RAV4", "Highlander", "Land Cruiser", "Hilux", "Prius", "Mirai",
    "Supra", "GR86", "GR Yaris", "GR Corolla", "bZ4X", "Proace", "Proace City",
  ],
  "Honda": [
    "Jazz", "Civic", "Civic Type R", "Accord", "Insight", "HR-V", "CR-V", "ZR-V", "e:Ny1",
    "Pilot", "Passport", "Ridgeline", "NSX", "e",
  ],
  "Nissan": [
    "Micra", "Note", "Leaf", "Juke", "Qashqai", "X-Trail", "Pathfinder",
    "Ariya", "350Z", "370Z", "GT-R", "Navara", "NV200", "NV300",
  ],
  "Mazda": [
    "Mazda2", "Mazda3", "Mazda6", "MX-5", "MX-30", "CX-3", "CX-30", "CX-5", "CX-60", "CX-80",
  ],
  "Subaru": [
    "Impreza", "WRX", "WRX STI", "Legacy", "Outback", "Forester", "XV", "Crosstrek",
    "BRZ", "Solterra", "Levorg", "Ascent",
  ],
  "Mitsubishi": [
    "Space Star", "Colt", "ASX", "Eclipse Cross", "Outlander", "Outlander PHEV",
    "Pajero", "Pajero Sport", "L200", "Lancer", "Lancer Evolution",
  ],
  "Lexus": [
    "CT", "IS", "ES", "LS", "RC", "LC", "UX", "NX", "RX", "RZ", "GX", "LX", "LM", "LFA",
  ],
  "Suzuki": [
    "Ignis", "Swift", "Swift Sport", "Baleno", "Across", "Jimny", "Vitara", "S-Cross", "SX4",
  ],
  "Infiniti": ["Q30", "Q50", "Q60", "Q70", "QX30", "QX50", "QX55", "QX60", "QX70", "QX80"],

  // ---------- KOREAN ----------
  "Hyundai": [
    "i10", "i20", "i20 N", "i30", "i30 N", "i40", "Ioniq", "Ioniq 5", "Ioniq 5 N", "Ioniq 6",
    "Kona", "Kona N", "Bayon", "Tucson", "Santa Fe", "Nexo", "Staria",
  ],
  "Kia": [
    "Picanto", "Rio", "Stonic", "Ceed", "Ceed SW", "ProCeed", "XCeed",
    "Niro", "EV3", "EV6", "EV9", "Sportage", "Sorento", "Stinger", "Soul", "Carnival",
  ],
  "Genesis": ["G70", "G80", "G90", "GV60", "GV70", "GV80"],
  "SsangYong": ["Tivoli", "Korando", "Rexton", "Musso", "Torres"],

  // ---------- FRENCH ----------
  "Renault": [
    "Clio", "Clio E-Tech", "Captur", "Mégane", "Mégane E-Tech", "Scénic", "Scenic E-Tech",
    "Talisman", "Espace", "Kadjar", "Koleos", "Arkana", "Twingo", "ZOE", "Austral", "Rafale",
    "Trafic", "Master", "Kangoo",
  ],
  "Peugeot": [
    "108", "208", "208 GT", "e-208", "308", "308 SW", "408", "508", "508 SW",
    "2008", "e-2008", "3008", "5008", "Rifter", "Partner", "Expert", "Traveller",
  ],
  "Citroën": [
    "C1", "C3", "C3 Aircross", "C4", "C4 X", "C5 Aircross", "C5 X",
    "DS3", "DS4", "DS5", "Berlingo", "SpaceTourer", "Jumpy", "Jumper", "ë-C4",
  ],
  "DS": ["DS3 Crossback", "DS4", "DS7 Crossback", "DS9"],
  "Alpine": ["A110", "A290", "A310"],

  // ---------- ITALIAN ----------
  "Fiat": [
    "500", "500e", "500X", "500L", "Panda", "Punto", "Tipo", "124 Spider", "Doblò", "Ducato",
  ],
  "Alfa Romeo": ["MiTo", "Giulietta", "Giulia", "Stelvio", "Tonale", "4C", "8C", "Brera", "Spider"],
  "Lancia": ["Ypsilon", "Delta", "Musa"],
  "Maserati": ["Ghibli", "Quattroporte", "Levante", "GranTurismo", "GranCabrio", "MC20", "Grecale"],
  "Ferrari": [
    "F8 Tributo", "488", "458 Italia", "812 Superfast", "Roma", "Portofino", "SF90 Stradale",
    "Purosangue", "296 GTB", "LaFerrari", "California",
  ],
  "Lamborghini": ["Huracán", "Aventador", "Urus", "Revuelto", "Gallardo", "Murciélago"],
  "Pagani": ["Huayra", "Zonda", "Utopia"],

  // ---------- SPANISH ----------
  "Seat": ["Ibiza", "Leon", "Leon Sportstourer", "Arona", "Ateca", "Tarraco", "Mii", "Alhambra"],
  "Cupra": ["Born", "Leon", "Formentor", "Ateca", "Tavascan", "Terramar"],

  // ---------- BRITISH ----------
  "Jaguar": [
    "XE", "XF", "XJ", "F-Type", "F-Pace", "E-Pace", "I-Pace",
  ],
  "Land Rover": [
    "Defender", "Discovery", "Discovery Sport", "Range Rover", "Range Rover Sport",
    "Range Rover Velar", "Range Rover Evoque", "Freelander",
  ],
  "Mini": [
    "Mini One", "Cooper", "Cooper S", "Cooper SE", "John Cooper Works",
    "Clubman", "Countryman", "Cabrio", "Paceman",
  ],
  "Aston Martin": ["Vantage", "DB9", "DB11", "DB12", "DBS", "Rapide", "Vanquish", "DBX"],
  "Bentley": ["Continental GT", "Flying Spur", "Bentayga", "Mulsanne"],
  "Rolls-Royce": ["Phantom", "Ghost", "Wraith", "Dawn", "Cullinan", "Spectre"],
  "McLaren": ["570S", "600LT", "650S", "720S", "750S", "Artura", "GT", "P1", "Senna"],
  "Lotus": ["Elise", "Exige", "Evora", "Emira", "Eletre", "Emeya"],
  "Morgan": ["Plus Four", "Plus Six", "Super 3"],
  "MG": ["MG3", "MG4", "MG5", "ZS", "ZS EV", "Marvel R", "Cyberster"],

  // ---------- AMERICAN ----------
  "Ford": [
    "Fiesta", "Focus", "Focus ST", "Focus RS", "Mondeo", "Kuga", "Puma", "EcoSport",
    "Galaxy", "S-Max", "Mustang", "Mustang Mach-E", "GT",
    "Ranger", "Ranger Raptor", "F-150", "F-150 Lightning", "Bronco", "Edge", "Explorer",
    "Transit", "Transit Custom", "Tourneo",
  ],
  "Chevrolet": ["Spark", "Camaro", "Corvette", "Tahoe", "Suburban", "Silverado", "Bolt", "Cruze", "Captiva"],
  "Cadillac": ["ATS", "CTS", "CT4", "CT5", "CT6", "XT4", "XT5", "XT6", "Escalade", "LYRIQ", "Celestiq"],
  "Chrysler": ["300C", "Voyager", "Pacifica"],
  "Dodge": ["Challenger", "Charger", "Durango", "Ram 1500", "Hornet"],
  "Jeep": [
    "Renegade", "Compass", "Cherokee", "Grand Cherokee", "Wrangler", "Gladiator",
    "Avenger", "Wagoneer", "Grand Wagoneer",
  ],
  "GMC": ["Sierra", "Yukon", "Acadia", "Terrain", "Hummer EV"],
  "Lincoln": ["Navigator", "Aviator", "Nautilus", "Corsair"],
  "Buick": ["Encore", "Enclave", "Envision", "LaCrosse"],
  "RAM": ["1500", "2500", "3500", "ProMaster", "Rampage", "TRX"],
  "Hummer": ["H1", "H2", "H3", "EV Pickup", "EV SUV"],

  // ---------- SWEDISH / CZECH ----------
  "Volvo": [
    "S40", "S60", "S60 Recharge", "S80", "S90", "V40", "V50", "V60", "V60 Cross Country",
    "V70", "V90", "V90 Cross Country", "XC40", "XC40 Recharge", "EX30", "EX40", "EX90",
    "XC60", "XC60 Recharge", "XC70", "XC90", "C40 Recharge", "C30",
  ],
  "Skoda": [
    "Citigo", "Fabia", "Scala", "Rapid", "Octavia", "Octavia Combi", "Octavia RS",
    "Superb", "Superb Combi", "Kamiq", "Karoq", "Kodiaq", "Enyaq", "Enyaq Coupé",
  ],
  "Saab": ["9-3", "9-5", "9-7X"],

  // ---------- EASTERN EURO / BUDGET ----------
  "Dacia": ["Sandero", "Sandero Stepway", "Logan", "Duster", "Lodgy", "Dokker", "Spring", "Jogger", "Bigster"],
  "Lada": ["Niva", "Vesta", "XRAY", "Granta", "Largus"],

  // ---------- CHINESE EV / NEW-ENERGY ----------
  "BYD": [
    "Atto 2", "Atto 3", "Dolphin", "Seal", "Seal U", "Seal U DM-i", "Han", "Tang",
    "Song Plus", "Song L", "Sealion 6", "Sealion 7", "Yuan Plus", "Qin Plus", "Frigate 07",
  ],
  "NIO": [
    "ES6", "ES7", "ES8", "EC6", "EC7", "ET5", "ET5 Touring", "ET7", "ET9", "EL6", "EL7", "EL8",
  ],
  "XPeng": [
    "G3", "G3i", "G6", "G9", "P5", "P7", "P7+", "X9", "Mona M03",
  ],
  "Zeekr": [
    "001", "001 FR", "007", "009", "X", "Mix",
  ],
  "Lynk & Co": ["01", "02", "03", "05", "06", "07", "08", "09", "Zero"],
  "Aiways": ["U5", "U6"],
  "Hongqi": ["E-HS9", "HS5", "HS7", "H5", "H9", "EH7", "E-QM5"],
  "GWM": [
    "Wey 03", "Wey 05", "Wey 06", "Wey 07",
    "Tank 300", "Tank 500", "Tank 700",
    "Haval H6", "Haval Jolion", "Haval Big Dog",
    "Ora 03", "Ora 07", "Ora Funky Cat",
    "Poer P-Series",
  ],
  "Chery": ["Omoda 5", "Omoda E5", "Tiggo 4", "Tiggo 7 Pro", "Tiggo 8 Pro", "Arrizo 5", "Jaecoo 7"],
  "Geely": ["Coolray", "Tugella", "Atlas Pro", "Monjaro", "Geometry C", "Galaxy E5"],
  "Leapmotor": ["T03", "C10", "C01", "C16", "B10"],
  "Voyah": ["Free", "Dream", "Courage", "Passion"],
  "Maxus": ["Mifa 9", "T90 EV", "Euniq 5", "Euniq 6", "G50", "G90"],
  "JAC": ["e-S2", "iEV7S", "iEVS4"],
  "Avatr": ["11", "12"],
  "Denza": ["N7", "N8", "D9", "Z9"],
  "Nezha": ["U", "S", "X", "GT"],
  "Lotus Tech": ["Eletre", "Emeya"],

  // ---------- AMERICAN COMPLETE COVERAGE ----------
  "Ford": [
    "Fiesta", "Focus", "Focus Estate", "Mondeo", "Galaxy", "S-Max", "Tourneo",
    "Puma", "EcoSport", "Kuga", "Edge", "Explorer", "Expedition",
    "Mustang", "Mustang Mach-E", "Mustang GT", "Mustang Shelby",
    "Ranger", "Ranger Raptor", "F-150", "F-150 Lightning", "F-150 Raptor", "F-250", "F-350",
    "Bronco", "Bronco Sport", "Maverick", "Transit", "Transit Custom",
    "GT", "Capri", "Escape",
  ],
  "Chevrolet": [
    "Camaro", "Camaro ZL1", "Corvette", "Corvette C8", "Corvette Z06", "Corvette E-Ray",
    "Tahoe", "Suburban", "Silverado", "Silverado EV", "Colorado", "Bolt", "Bolt EUV",
    "Equinox", "Equinox EV", "Trailblazer", "Blazer", "Blazer EV", "Trax",
    "Spark", "Malibu", "Impala", "Cruze",
  ],
  "Cadillac": ["CT4", "CT4-V", "CT5", "CT5-V", "CT5 Blackwing", "Escalade", "Escalade ESV", "XT4", "XT5", "XT6", "Lyriq", "Celestiq", "Vistiq"],
  "GMC": ["Sierra", "Sierra EV", "Yukon", "Yukon Denali", "Acadia", "Terrain", "Canyon", "Hummer EV SUV", "Hummer EV Pickup"],
  "Lincoln": ["Aviator", "Navigator", "Nautilus", "Corsair", "Continental"],
  "Buick": ["Encore GX", "Envision", "Enclave", "LaCrosse"],
  "Chrysler": ["300", "Pacifica", "Voyager"],
  "Dodge": ["Challenger", "Charger", "Durango", "Hornet", "Demon 170", "Charger Daytona EV", "RAM"],
  "Jeep": [
    "Renegade", "Compass", "Cherokee", "Grand Cherokee", "Grand Cherokee L", "Grand Cherokee 4xe",
    "Wrangler", "Wrangler 4xe", "Wrangler Rubicon", "Gladiator", "Avenger", "Wagoneer", "Grand Wagoneer",
  ],
  "RAM": ["1500", "1500 TRX", "1500 REV", "2500", "3500", "ProMaster"],
  "Hummer": ["EV Pickup", "EV SUV", "H1", "H2", "H3"],
  "Tesla": [
    "Model S", "Model S Plaid", "Model 3", "Model 3 Performance", "Model 3 Long Range",
    "Model X", "Model X Plaid", "Model Y", "Model Y Long Range", "Model Y Performance",
    "Cybertruck", "Cybertruck Cyberbeast", "Roadster", "Semi",
  ],
  "Rivian": ["R1S", "R1T", "R2", "R3", "R3X"],
  "Lucid": ["Air Pure", "Air Touring", "Air Grand Touring", "Air Sapphire", "Gravity"],
  "Fisker": ["Ocean", "Pear"],

  // ---------- JAPANESE COMPLETE COVERAGE ----------
  "Toyota": [
    "Aygo X", "Yaris", "Yaris Cross", "Corolla", "Corolla Touring Sports", "Corolla Cross",
    "Camry", "Crown", "Avensis", "Auris",
    "C-HR", "RAV4", "Highlander", "Land Cruiser", "Land Cruiser Prado", "4Runner", "Sequoia",
    "Hilux", "Tundra", "Tacoma",
    "Prius", "Prius Prime", "Mirai", "Supra", "GR Supra", "GR86", "GR Yaris", "GR Corolla",
    "bZ4X", "bZ3", "bZ3X", "Urban Cruiser",
    "Proace", "Proace Verso", "Proace City",
  ],
  "Honda": [
    "Jazz", "Civic", "Civic Type R", "Accord", "Insight", "Clarity",
    "HR-V", "CR-V", "Passport", "Pilot", "Ridgeline", "Odyssey",
    "ZR-V", "WR-V", "Prologue", "e:Ny1", "e",
    "NSX",
  ],
  "Mazda": [
    "Mazda2", "Mazda3", "Mazda3 Hatchback", "Mazda6", "MX-5", "MX-30", "MX-30 R-EV",
    "CX-3", "CX-30", "CX-5", "CX-50", "CX-60", "CX-70", "CX-80", "CX-90",
  ],
  "Nissan": [
    "Micra", "Note", "Juke", "Qashqai", "X-Trail", "Rogue", "Pathfinder", "Murano", "Armada",
    "Leaf", "Ariya", "Townstar", "Townstar EV", "Primastar", "Interstar",
    "350Z", "370Z", "Z", "GT-R", "GT-R Nismo",
    "Frontier", "Titan", "Navara",
  ],
  "Subaru": [
    "Impreza", "WRX", "WRX STI", "Levorg", "Legacy", "Outback",
    "XV", "Crosstrek", "Forester", "Ascent", "BRZ", "Solterra",
  ],
  "Suzuki": [
    "Swift", "Swift Sport", "Ignis", "Baleno", "Vitara", "S-Cross", "Jimny", "Across", "Swace",
    "Grand Vitara", "Fronx", "Across PHEV",
  ],
  "Mitsubishi": [
    "Space Star", "Mirage", "ASX", "Eclipse Cross", "Outlander", "Outlander PHEV", "L200",
    "Pajero", "Pajero Sport", "Lancer", "Lancer Evolution", "Colt",
  ],
  "Lexus": [
    "CT", "IS", "IS F", "ES", "GS", "GS F", "LS",
    "UX", "UX 300e", "NX", "NX 350h", "NX 450h+", "RX", "RX 500h", "GX", "LX",
    "RC", "RC F", "LC", "LC 500", "LFA",
    "RZ", "LBX", "TX",
  ],
  "Infiniti": ["Q30", "Q50", "Q60", "Q70", "QX30", "QX50", "QX55", "QX60", "QX70", "QX80"],
  "Acura": ["ILX", "TLX", "TLX Type S", "RDX", "MDX", "MDX Type S", "Integra", "Integra Type S", "ZDX", "NSX"],

  // ---------- KOREAN COMPLETE COVERAGE ----------
  "Hyundai": [
    "i10", "i20", "i20 N", "i30", "i30 N", "i40", "Bayon",
    "Kona", "Kona Electric", "Kona N", "Tucson", "Tucson PHEV", "Santa Fe", "Palisade",
    "IONIQ 5", "IONIQ 5 N", "IONIQ 6", "IONIQ 7", "IONIQ 9",
    "Sonata", "Elantra", "Elantra N", "Nexo", "Staria", "Inster", "Casper",
  ],
  "Kia": [
    "Picanto", "Rio", "Stonic", "Ceed", "Ceed SW", "ProCeed", "XCeed",
    "Niro", "Niro EV", "Sportage", "Sorento", "Telluride", "Carnival", "Carens",
    "EV3", "EV5", "EV6", "EV6 GT", "EV9", "EV4",
    "K5", "K7", "K9", "Stinger", "Soul",
  ],
  "Genesis": ["G70", "G80", "G80 Electrified", "G90", "GV60", "GV70", "GV70 Electrified", "GV80", "GV80 Coupé", "X Concept"],
  "SsangYong": ["Tivoli", "Korando", "Rexton", "Musso", "Torres", "Torres EVX"],

  // ---------- FRENCH ----------
  "Renault": [
    "Twingo", "Clio", "Megane", "Megane E-Tech", "Captur", "Arkana", "Kadjar", "Austral",
    "Scenic", "Scenic E-Tech", "Espace", "Talisman", "Koleos",
    "Zoe", "Twizy", "5 E-Tech", "4 E-Tech", "Master", "Trafic", "Kangoo", "Kangoo E-Tech",
    "Rafale", "Alpine A110", "Symbioz",
  ],
  "Peugeot": [
    "108", "208", "208 E", "2008", "2008 E", "308", "308 SW", "308 E", "3008", "3008 E",
    "408", "408 E", "508", "508 SW", "5008", "5008 E", "Rifter", "Partner", "Traveller",
    "Boxer", "Expert", "Landtrek", "e-Rifter", "e-Traveller",
  ],
  "Citroën": [
    "C3", "C3 Aircross", "C4", "C4 X", "C5 Aircross", "C5 X", "Berlingo", "SpaceTourer",
    "ë-C3", "ë-C4", "ë-C4 X", "ë-Berlingo", "ë-SpaceTourer", "ë-Jumpy", "ë-Jumper",
    "DS3", "Ami",
  ],
  "DS": ["DS 3", "DS 3 Crossback", "DS 4", "DS 7", "DS 7 Crossback", "DS 9"],
  "Alpine": ["A110", "A110 S", "A110 R", "A290", "A390"],

  // ---------- ITALIAN / SPANISH ----------
  "Fiat": [
    "500", "500 Hybrid", "500e", "500X", "500L", "Panda", "Panda Cross", "Tipo", "Tipo Cross",
    "600e", "Topolino", "Fullback", "Doblò", "Ducato", "Talento", "Bravo", "Punto",
  ],
  "Lancia": ["Ypsilon", "Ypsilon EV", "Delta", "Gamma", "Thema"],
  "Abarth": ["595", "695", "500e", "600e", "Pulse"],
  "Alfa Romeo": [
    "Giulia", "Giulia Quadrifoglio", "Stelvio", "Stelvio Quadrifoglio", "Tonale",
    "MiTo", "Giulietta", "4C", "8C", "Junior", "Junior Veloce",
  ],
  "Maserati": [
    "Ghibli", "Quattroporte", "Levante", "GranTurismo", "GranCabrio", "MC20", "MC20 Cielo", "Grecale",
  ],
  "Ferrari": [
    "F8", "F8 Tributo", "F8 Spider", "Roma", "Roma Spider", "Portofino", "Portofino M",
    "812 Superfast", "812 GTS", "812 Competizione", "SF90", "SF90 Stradale", "SF90 Spider",
    "296 GTB", "296 GTS", "Purosangue", "12Cilindri", "12Cilindri Spider", "Daytona SP3",
    "488", "488 Pista", "458", "California", "FF", "GTC4Lusso", "Enzo", "F40", "F50", "LaFerrari",
  ],
  "Lamborghini": [
    "Huracán", "Huracán EVO", "Huracán STO", "Huracán Tecnica", "Huracán Sterrato",
    "Aventador", "Aventador SVJ", "Revuelto", "Urus", "Urus S", "Urus Performante",
    "Temerario", "Lanzador", "Sián", "Countach LPI 800-4", "Diablo", "Murciélago", "Gallardo",
  ],
  "Pagani": ["Huayra", "Huayra Roadster", "Huayra BC", "Utopia", "Zonda", "Zonda R"],
  "SEAT": [
    "Mii", "Ibiza", "Leon", "Leon Sportstourer", "Arona", "Ateca", "Tarraco", "Alhambra", "Toledo",
  ],
  "Cupra": [
    "Leon", "Leon Sportstourer", "Formentor", "Ateca", "Born", "Tavascan", "Terramar", "Raval",
  ],

  // ---------- BRITISH ----------
  "Jaguar": ["XE", "XF", "XJ", "F-Pace", "F-Pace SVR", "E-Pace", "I-Pace", "F-Type", "F-Type R"],
  "Land Rover": [
    "Defender 90", "Defender 110", "Defender 130", "Defender Octa",
    "Discovery", "Discovery Sport", "Freelander",
    "Range Rover", "Range Rover SV", "Range Rover Sport", "Range Rover Sport SV",
    "Range Rover Velar", "Range Rover Evoque",
  ],
  "Mini": [
    "Cooper", "Cooper S", "Cooper SE", "Cooper Convertible", "Cooper SD",
    "Clubman", "Countryman", "Countryman SE", "John Cooper Works", "Aceman", "Paceman",
  ],
  "Bentley": [
    "Continental GT", "Continental GTC", "Continental Supersports", "Flying Spur", "Bentayga", "Bentayga EWB", "Mulsanne", "Batur",
  ],
  "Rolls-Royce": ["Phantom", "Ghost", "Ghost Black Badge", "Wraith", "Dawn", "Cullinan", "Cullinan Black Badge", "Spectre"],
  "Aston Martin": [
    "Vantage", "Vantage F1", "DB11", "DB12", "DB12 Volante", "DBS", "DBS Superleggera", "DBX", "DBX707", "Valkyrie", "Valhalla", "Vanquish",
  ],
  "McLaren": ["540C", "570S", "570S Spider", "600LT", "620R", "650S", "675LT", "720S", "750S", "765LT", "Artura", "GT", "GTS", "Senna", "Speedtail", "P1", "Solus GT", "W1"],
  "Lotus": ["Elise", "Exige", "Evora", "Emira", "Eletre", "Emeya", "Evija"],
  "Morgan": ["Plus Four", "Plus Six", "3 Wheeler", "Super 3"],
  "TVR": ["Griffith", "Sagaris", "Tuscan"],
  "MG": [
    "MG3", "MG3 Hybrid+", "MG4 EV", "MG4 XPower", "MG5 EV", "ZS", "ZS EV", "HS", "HS PHEV",
    "Marvel R", "Cyberster", "MG7", "Cyber GTS",
  ],

  // ---------- SWEDISH / CZECH / OTHER NORDIC ----------
  "Volvo": [
    "EX30", "EX40", "EX60", "EX90", "EM90",
    "XC40", "XC40 Recharge", "XC60", "XC90", "XC70",
    "S60", "S90", "V60", "V60 Cross Country", "V90", "V90 Cross Country",
    "C40 Recharge", "C30", "C70",
  ],
  "Polestar": ["1", "2", "3", "4", "5", "6"],
  "Koenigsegg": ["Agera", "Agera RS", "Jesko", "Jesko Absolut", "Regera", "Gemera", "CC850"],
  "Saab": ["9-3", "9-3 Convertible", "9-5", "9-7X", "900"],

  // ---------- OTHER GLOBAL ----------
  "Smart": ["fortwo", "fortwo electric", "forfour", "#1", "#3", "#5"],
  "Isuzu": ["D-Max", "MU-X", "Trooper", "VehiCROSS"],
  "Tata": ["Punch", "Nexon", "Nexon EV", "Tigor EV", "Harrier", "Safari", "Curvv", "Curvv EV", "Sierra"],
  "Mahindra": ["Thar", "Thar Roxx", "XUV300", "XUV400", "XUV700", "Scorpio N", "BE 6", "XEV 9e"],
  "Holden": ["Commodore", "Monaro", "Statesman"],

  // ---------- HYPER / NICHE SUPERCARS ----------
  "Bugatti": ["Veyron", "Chiron", "Chiron Pur Sport", "Chiron Super Sport", "Centodieci", "Divo", "Bolide", "Tourbillon", "Mistral"],
  "Rimac": ["Concept One", "Nevera", "Nevera R"],
  "Maybach": ["S 580", "S 680", "GLS 600", "EQS SUV", "EQS 680"],
};

/** Returns models for a given brand, or empty array if not known. */
export const getModelsForBrand = (brand) => BRAND_MODELS[brand] || [];

/** Sorted union of all model strings (used for global suggestion / sitemap). */
export const ALL_MODELS = Object.values(BRAND_MODELS).flat();
